import React, { useState, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { jwtDecode } from 'jwt-decode';
import axios from "axios";

/*
Handles access token retrieval/refreshing.
*/
const ProtectedRoute = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(null); // Track authentication status
    const [authToken, setAuthToken] = useState(localStorage.getItem("authToken"));
    const refreshToken = localStorage.getItem("refreshToken");
    const navigate = useNavigate();

    const handleTokenRefresh = async () => {
        try {
            if (!refreshToken) {
                setIsAuthenticated(false);
                return;
            }
            const apiUrl = process.env.REACT_APP_API_URL;
            const response = await axios.post(`${apiUrl}/api/refresh_token/`, { refresh: refreshToken }, { headers: { "Content-Type": "application/json" } });
            const newAuthToken = response.data.access;
            localStorage.setItem("authToken", newAuthToken);
            setAuthToken(newAuthToken); // Update state to trigger useEffect
        } catch (error) {
            console.error("Error refreshing token:", error);
            localStorage.removeItem("authToken");
            localStorage.removeItem("refreshToken");
            setIsAuthenticated(false);
        }
    };

    const logoutUser = () => {
        localStorage.removeItem("authToken");
        localStorage.removeItem("refreshToken");
        setIsAuthenticated(false);
        navigate("/login", { replace: true });
    };

    useEffect(() => {
        const scheduleTokenRefresh = () => {
            if (!authToken) return;
            const { exp } = jwtDecode(authToken); // Decode the token to get the `exp` time
            const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
            const timeUntilExpiration = exp - currentTime;
            if (timeUntilExpiration <= 0) { handleTokenRefresh(); } // Token is already expired, refresh immediately     
            else {
                // Schedule refresh 60 seconds before expiration
                const refreshTimeout = setTimeout(handleTokenRefresh, (timeUntilExpiration - 60) * 1000);
                return () => clearTimeout(refreshTimeout); // Cleanup timeout on unmount or token change
            }
        };

        scheduleTokenRefresh();
    }, [authToken]);

    //log user out if access token isn't refreshed for 10 hours (set in settings.py with SIMPLE_JWT)
    useEffect(() => {
        if (!authToken) return; // No token, nothing to do
        try {
            const { exp } = jwtDecode(authToken); //same as writing jwtDecode(authToken).exp. DRF creates an object for the access token on the server with this exp field.
            const currentTime = Math.floor(Date.now() / 1000);
            const timeUntilExpiration = exp - currentTime;
            if (timeUntilExpiration > 0) {
                // Schedule auto-logout exactly when the token expires
                const logoutTimer = setTimeout(() => {
                    logoutUser();
                }, timeUntilExpiration * 1000);
                return () => clearTimeout(logoutTimer); // Cleanup on unmount or token change
            }
        } catch (error) { logoutUser(); }// If the token is invalid, log out immediately
    }, [authToken]); // Runs every time authToken changes

    useEffect(() => {
        const verifyToken = async () => {
            if (!authToken) {
                setIsAuthenticated(false); // No token found
                return;
            }
            try {
                const apiUrl = process.env.REACT_APP_API_URL;
                const response = await axios.post(
                    `${apiUrl}/api/verify_token/`,
                    { token: authToken },
                    { headers: { "Content-Type": "application/json" } }
                );
                if (response.status === 200 && response.data.valid) { setIsAuthenticated(true); } 
                else { throw new Error("Invalid token"); }
            } catch (error) { await handleTokenRefresh(); } //invalid token. attempt refresh.
        };
        verifyToken();
    }, [authToken]);

    if (isAuthenticated === null) { return null; } // Show loading state
    return isAuthenticated ? children : <Navigate to="/login" replace />;
};

export default ProtectedRoute;